import { BanIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOM_CHART_COLORS, type CustomChartColor } from '@/entities/dashboards';
import { cn } from '@/shared/lib';
import { IconButton } from '@/shared/ui';
import { slicePaletteBgClassByCode } from '../utils';
import { chartColorLabelKey } from './chartColors';

type Props = {
  /** Выбранный цвет; не задан — элемент красится тем, чем красился бы без выбора */
  value?: CustomChartColor;
  onChange: (color: CustomChartColor | undefined) => void;
  /** Показать образец «цвет по умолчанию» — им выбранный цвет снимается */
  isResettable?: boolean;
};

/**
 * Выбор цвета из набора цветов диаграмм: десять цветов палитры долей и три цвета шкалы зон.
 *
 * Цвет выбирается образцами, а не перечнем названий: выбирают его глазами, и название цвета
 * читателю не нужно — оно нужно лишь тому, кто ведёт выбор с клавиатуры, и потому стоит подсказкой.
 *
 * Произвольного цвета в выборе нет: набор подобран так, чтобы диаграммы дашборда не рябили и не
 * спорили друг с другом, а произвольный цвет это условие снимает.
 */
export const ChartColorPicker: FC<Props> = ({ value, onChange, isResettable }) => {
  const { t } = useTranslation('qualityDashboard');

  return (
    <div className="flex flex-wrap items-center gap-1">
      {isResettable && (
        <IconButton
          variant="ghost"
          size="icon"
          label={t('presets.editor.colorDefault')}
          aria-pressed={value === undefined}
          icon={<BanIcon className="size-4 text-muted-foreground" />}
          className={cn('size-7', value === undefined && 'ring-2 ring-primary')}
          onClick={() => onChange(undefined)}
        />
      )}
      {CUSTOM_CHART_COLORS.map((color, index) => {
        const label = t(chartColorLabelKey(color), { number: index + 1 });
        return (
          <IconButton
            key={color}
            variant="ghost"
            size="icon"
            label={label}
            aria-pressed={value === color}
            icon={<span className={cn('size-4 rounded-full', slicePaletteBgClassByCode(color))} />}
            className={cn('size-7', value === color && 'ring-2 ring-primary')}
            onClick={() => onChange(color)}
          />
        );
      })}
    </div>
  );
};
